import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getDiscounts, createDiscount, updateDiscount, toggleDiscount, deleteDiscount } from "store/discounts"

export const useDiscounts = () => {
  const dispatch = useDispatch()
  const discounts = useSelector((state) => state.discounts.items)
  const metadata = useSelector((state) => state.discounts.metadata)
  const isLoading = useSelector((state) => state.discounts.loading)
  const totalExperiences = useSelector((state) => state?.trips?.items?.length ?? 0)
  const isMutating = useSelector(
    (state) =>
      state.discounts.createDiscount.loading ||
      state.discounts.updateDiscount.loading ||
      state.discounts.deleteDiscount.loading ||
      state.discounts.toggleDiscount.loading
  )

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Create / edit modal
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [discountToEdit, setDiscountToEdit] = useState(null)

  // Delete confirmation
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [discountToDelete, setDiscountToDelete] = useState(null)

  // Toggle status warning
  const [isToggleWarningOpen, setIsToggleWarningOpen] = useState(false)
  const [discountToToggle, setDiscountToToggle] = useState(null)

  // Assign experiences modal
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
  const [discountToAssign, setDiscountToAssign] = useState(null)

  const openAssignModal = (record) => {
    setDiscountToAssign(record)
    setIsAssignModalOpen(true)
  }

  const closeAssignModal = () => {
    setIsAssignModalOpen(false)
    setDiscountToAssign(null)
  }

  const handleAssignSave = async (selectedExperienceIds) => {
    const current = discountToAssign?.applicable_experiences
    const currentIds = Array.isArray(current) ? current.map(String).sort((a, b) => a.localeCompare(b)) : []
    const nextIds = selectedExperienceIds.map(String).sort((a, b) => a.localeCompare(b))
    if (JSON.stringify(currentIds) === JSON.stringify(nextIds)) {
      closeAssignModal()
      return
    }

    const allSelected = totalExperiences > 0 && selectedExperienceIds.length === totalExperiences
    const applicable_experiences = allSelected ? "all" : selectedExperienceIds.map(Number)
    await dispatch(updateDiscount(discountToAssign.id, { applicable_experiences }))
    dispatch(getDiscounts({ page, per_page: pageSize }))
    closeAssignModal()
  }

  useEffect(() => {
    dispatch(getDiscounts({ page, per_page: pageSize }))
  }, [dispatch, page, pageSize])

  const handlePageChange = (newPage) => setPage(newPage)

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize)
    setPage(1)
  }

  const openAddModal = () => {
    setDiscountToEdit(null)
    setIsModalOpen(true)
  }

  const openEditModal = (record) => {
    setDiscountToEdit(record)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setDiscountToEdit(null)
  }

  const handleSave = async (values) => {
    if (discountToEdit) {
      await dispatch(updateDiscount(discountToEdit.id, values))
    } else {
      await dispatch(createDiscount(values))
    }
    dispatch(getDiscounts({ page, per_page: pageSize }))
    closeModal()
  }

  const handleDeleteRequest = (record) => {
    setDiscountToDelete(record)
    setIsDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    await dispatch(deleteDiscount(discountToDelete.id))
    dispatch(getDiscounts({ page, per_page: pageSize }))
    setIsDeleteModalOpen(false)
    setDiscountToDelete(null)
  }

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false)
    setDiscountToDelete(null)
  }

  const handleToggleRequest = (record) => {
    setDiscountToToggle(record)
    setIsToggleWarningOpen(true)
  }

  const handleToggleConfirm = async () => {
    await dispatch(toggleDiscount(discountToToggle.id))
    dispatch(getDiscounts({ page, per_page: pageSize }))
    setIsToggleWarningOpen(false)
    setDiscountToToggle(null)
  }

  const handleToggleCancel = () => {
    setIsToggleWarningOpen(false)
    setDiscountToToggle(null)
  }

  const getMenuItems = (record) => [
    {
      key: "edit",
      label: "Edit",
      onClick: () => openEditModal(record)
    },
    {
      key: "toggle",
      label: record.status === "active" ? "Deactivate" : "Activate",
      onClick: () => handleToggleRequest(record)
    },
    {
      key: "delete",
      label: "Delete",
      danger: true,
      onClick: () => handleDeleteRequest(record)
    }
  ]

  return {
    discounts,
    isLoading,
    isMutating,
    metadata,
    page,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    isModalOpen,
    discountToEdit,
    openAddModal,
    closeModal,
    handleSave,
    isDeleteModalOpen,
    discountToDelete,
    handleDeleteRequest,
    handleDeleteConfirm,
    handleDeleteCancel,
    isToggleWarningOpen,
    discountToToggle,
    handleToggleConfirm,
    handleToggleCancel,
    getMenuItems,
    isAssignModalOpen,
    discountToAssign,
    openAssignModal,
    closeAssignModal,
    handleAssignSave
  }
}
